import React, { useState, useMemo, useCallback, useRef } from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ChartLegendSeries {
  key: string;
  name: string;
  color: string;
  value?: number | string | null;
  /** Per-series numeric value formatter (used by the legend strip). */
  formatter?: (value: number) => string;
}

type SortMode = 'default' | 'value-asc' | 'value-desc';

export interface ChartLegendProps {
  series: ChartLegendSeries[];
  hiddenKeys: Set<string>;
  onToggle: (key: string) => void;
  onToggleAll?: (visible: boolean) => void;
  /** Title shown at the top of the legend */
  title?: string;
  valueFormatter?: (value: number | string) => string;
  defaultCollapsed?: boolean;
  maxHeight?: number;
  defaultPosition?: { x: number; y: number };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ChartLegend: React.FC<ChartLegendProps> = ({
  series,
  hiddenKeys,
  onToggle,
  onToggleAll,
  title,
  valueFormatter,
  defaultCollapsed = false,
  maxHeight = 120,
  defaultPosition,
}) => {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const [sortMode, setSortMode] = useState<SortMode>('default');

  // Drag state
  const [pos, setPos] = useState(defaultPosition ?? { x: 0, y: 0 });
  const dragRef = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    // Only drag from the toolbar area (not from buttons)
    if ((e.target as HTMLElement).closest('button')) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { startX: e.clientX, startY: e.clientY, origX: pos.x, origY: pos.y };
  }, [pos]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    setPos({ x: dragRef.current.origX + dx, y: dragRef.current.origY + dy });
  }, []);

  const handlePointerUp = useCallback(() => {
    dragRef.current = null;
  }, []);

  const allHidden = hiddenKeys.size === series.length;
  const allVisible = hiddenKeys.size === 0;

  const cycleSortMode = useCallback(() => {
    setSortMode((prev) => {
      switch (prev) {
        case 'default': return 'value-desc';
        case 'value-desc': return 'value-asc';
        case 'value-asc': return 'default';
        default: return 'default';
      }
    });
  }, []);

  const sortedSeries = useMemo(() => {
    if (sortMode === 'default') return series;
    const sorted = [...series];
    switch (sortMode) {
      case 'value-asc': sorted.sort((a, b) => (toNum(a.value) ?? 0) - (toNum(b.value) ?? 0)); break;
      case 'value-desc': sorted.sort((a, b) => (toNum(b.value) ?? 0) - (toNum(a.value) ?? 0)); break;
    }
    return sorted;
  }, [series, sortMode]);

  const sortLabel = (() => {
    switch (sortMode) {
      case 'value-desc': return 'Val \u2193';
      case 'value-asc': return 'Val \u2191';
      default: return 'Sort';
    }
  })();

  const formatValue = (v: number | string | null | undefined) => {
    if (v == null) return '';
    if (valueFormatter) return valueFormatter(v);
    if (typeof v === 'number') return v.toLocaleString();
    return v;
  };

  return (
    <div
      className="select-none bg-surface/95 backdrop-blur-sm rounded-lg border border-neutral-200/50 shadow-sm"
      style={{ transform: `translate(${pos.x}px, ${pos.y}px)`, fontSize: '10px', lineHeight: '14px' }}
    >
      {/* Header — draggable handle */}
      <div
        className="flex items-center gap-1 px-1.5 py-0.5 cursor-grab active:cursor-grabbing"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        {collapsed ? (
          /* Collapsed: read as a legend (color swatches + label), not a stray drag grip */
          <div className="flex items-center gap-1">
            <span className="flex items-center gap-0.5">
              {sortedSeries.slice(0, 4).map((s) => (
                <span
                  key={s.key}
                  className="w-2 h-2 rounded-sm shrink-0"
                  style={{ backgroundColor: s.color, border: `1.5px solid ${s.color}` }}
                />
              ))}
            </span>
            <span className="text-neutral-500 font-medium truncate max-w-[120px]">
              {series.length === 1 ? series[0].name : `${series.length} series`}
            </span>
          </div>
        ) : (
          <svg className="w-3.5 h-3.5 text-neutral-300 shrink-0" viewBox="0 0 24 24" fill="currentColor">
            <circle cx="8" cy="6" r="1.5" /><circle cx="16" cy="6" r="1.5" />
            <circle cx="8" cy="12" r="1.5" /><circle cx="16" cy="12" r="1.5" />
            <circle cx="8" cy="18" r="1.5" /><circle cx="16" cy="18" r="1.5" />
          </svg>
        )}

        <button
          onClick={() => setCollapsed(!collapsed)}
          className="p-0.5 text-neutral-400 hover:text-neutral-600 transition-colors"
          title={collapsed ? 'Expand legend' : 'Collapse legend'}
        >
          <svg className={`w-3.5 h-3.5 transition-transform ${collapsed ? '-rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 12H5m7-7l7 7-7 7" />
          </svg>
        </button>

        {!collapsed && <div className="flex-1" />}

        {!collapsed && (
          <>
            {/* Sort — arrow icons */}
            <button
              onClick={cycleSortMode}
              className="p-0.5 text-neutral-400 hover:text-neutral-600 transition-colors"
              title={`Sort: ${sortLabel}`}
            >
              {sortMode === 'value-desc' ? (
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v14m0 0l-7-7m7 7l7-7" />
                </svg>
              ) : sortMode === 'value-asc' ? (
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19V5m0 0l-7 7m7-7l7 7" />
                </svg>
              ) : (
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 3v18m0 0l-4-4m4 4l4-4m6-14v18m0-18l-4 4m4-4l4 4" />
                </svg>
              )}
            </button>

            {/* Toggle all — eye icon */}
            {onToggleAll && (
              <button
                onClick={() => onToggleAll(allHidden)}
                className="p-0.5 text-neutral-400 hover:text-neutral-600 transition-colors"
                title={allVisible ? 'Hide all' : 'Show all'}
              >
                {allVisible ? (
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                ) : (
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                  </svg>
                )}
              </button>
            )}
          </>
        )}
      </div>

      {/* Series rows */}
      {!collapsed && (
        <div className="overflow-y-auto px-1 pb-0.5" style={{ maxHeight }}>
          {sortedSeries.map((s) => {
            const isHidden = hiddenKeys.has(s.key);
            return (
              <div
                key={s.key}
                className={`flex items-center gap-1 py-px px-1 rounded cursor-pointer transition-colors hover:bg-neutral-100/60 ${
                  isHidden ? 'opacity-35' : ''
                }`}
                onClick={() => onToggle(s.key)}
              >
                <span
                  className="w-2 h-2 rounded-sm shrink-0"
                  style={{ backgroundColor: isHidden ? 'transparent' : s.color, border: `1.5px solid ${s.color}` }}
                />
                <span className="text-neutral-600 font-medium truncate">{s.name}</span>
                {s.value != null && (
                  <span className="shrink-0 tabular-nums text-neutral-500">{formatValue(s.value)}</span>
                )}
                <button
                  onClick={(e) => { e.stopPropagation(); onToggle(s.key); }}
                  className="shrink-0 ml-auto text-neutral-300 hover:text-neutral-500 transition-colors"
                >
                  {isHidden ? (
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ChartLegend;

// ---------------------------------------------------------------------------
// Hook for managing legend state
// ---------------------------------------------------------------------------

export function useChartLegend() {
  const [hiddenKeys, setHiddenKeys] = useState<Set<string>>(new Set());

  const toggle = useCallback((key: string) => {
    setHiddenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const toggleAll = useCallback((show: boolean, allKeys?: string[]) => {
    if (show) {
      setHiddenKeys(new Set());
    } else if (allKeys) {
      setHiddenKeys(new Set(allKeys));
    }
  }, []);

  const setAllHidden = useCallback((keys: string[]) => {
    setHiddenKeys(new Set(keys));
  }, []);

  return { hiddenKeys, toggle, toggleAll, setAllHidden };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function toNum(v: unknown): number | null {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const n = parseFloat(v);
    return isNaN(n) ? null : n;
  }
  return null;
}
